import { FormParams } from "@helpers/utils";

export interface CartItem {
  id: string;
  quantity: number;
  properties: Record<string, string | string[] | Record<string, string>>;
  title: string;
  image: string;
  original_price: string;
  original_line_price: string;
  price: string;
  sku: string;
  key: string;
  product_has_only_default_variant: boolean;
  options_with_values: Record<string, string>[];
  variant_id: string;
  inventory_quantity: number;
}

export interface CartData {
  attributes: object;
  cart_level_discount_applications: string[];
  currency: string;
  item_count: number;
  items: CartItem[];
  items_subtotal_price: string;
  note: string | null;
  original_total_price: number;
  requires_shipping: boolean;
  token: string | null;
  total_discount: number;
  total_price: number;
  total_weight: number;
}

export const fallbackCartData: CartData = {
  attributes: {},
  cart_level_discount_applications: [],
  currency: "",
  item_count: 0,
  items: [],
  items_subtotal_price: "",
  note: null,
  original_total_price: 0,
  requires_shipping: false,
  token: null,
  total_discount: 0,
  total_price: 0,
  total_weight: 0,
};

export async function fetchCartData(): Promise<CartData> {
  const response = await fetch("/cart.js");
  return await response.json();
}

export async function sendAddToCart(
  items: {
    id: string;
    quantity: number;
    properties: Record<string, string | string[] | Record<string, string>>;
    selling_plan: string | string[] | Record<string, string>;
    pre_order: string;
    pre_order_dispatch_eta: string;
  }[],
): Promise<void> {
  let itemsWithPreOrder = items;

  console.log("sendAddToCart", items, items[0].pre_order_dispatch_eta)

    
  if (items[0].pre_order === "true") {

    const preOrderDateField = items[0].pre_order_dispatch_eta ? items[0].pre_order_dispatch_eta : '';

    function formatDispatchDate(isoDate: string): string {
      if (!isoDate) return "";
      const parts = isoDate.split("-").map((p) => parseInt(p, 10));
      if (parts.length !== 3) return isoDate;
      const [year, month, day] = parts;
      if (isNaN(year) || isNaN(month) || isNaN(day)) return isoDate;
    
      const monthNames = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
    
      const getOrdinal = (d: number): string => {
        const v = d % 100;
        if (v >= 11 && v <= 13) return "th";
        switch (d % 10) {
          case 1:
            return "st";
          case 2:
            return "nd";
          case 3:
            return "rd";
          default:
            return "th";
        }
      };
    
      const monthName = monthNames[Math.max(0, Math.min(11, month - 1))];
      return `${day}${getOrdinal(day)} ${monthName} ${year}`;
    }

    const formattedPreOrderDispatchDate =
      typeof preOrderDateField === "string"
        ? formatDispatchDate(preOrderDateField)
        : "";

    const preOrderShipDate = window.themeVars.strings.cart.preOrderShipDate.replace(
      "{{ date }}",
      formattedPreOrderDispatchDate,
    )

    itemsWithPreOrder = items.map((item) => ({
      ...item,
      properties: {
        ...(item.properties || {}),
        '_pre_order': 'true',
        'Estimated Dispatch Date': preOrderShipDate,
      },
    }));
  }
    
  const response = await fetch("/cart/add.js", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ items: itemsWithPreOrder }),
  });

  const cart = await response.json();

  if (cart.error || cart.status) {
    console.error(
      `Cart.sendAddToCart() : ${cart.description || cart.message || "Unknown error"}`,
    );
  } else {
    // Fetch the latest cart data
    const updatedCartData = await fetchCartData();
    // Dispatch an event with the updated cart data
    document.dispatchEvent(
      new CustomEvent("cart:added", { detail: updatedCartData }),
    );

    document.dispatchEvent(
      new CustomEvent("cart:updated", { detail: updatedCartData }),
    );
  }
}

export function addMultipleItems(
  items: {
    id: string;
    quantity: number;
    properties: Record<string, string | string[] | Record<string, string>>;
    selling_plan: string | string[] | Record<string, string>;
  }[],
): void {
  if (!items || !items.length) {
    return console.error("Cart.addMultipleItems() : items are required.");
  }

  sendAddToCart(items);
}

export function extractProperties(
  params: FormParams,
): Record<string, string | string[] | Record<string, string>> {
  const propertyInputKeys = Object.keys(params).filter((key) =>
    key.startsWith("properties"),
  );
  const properties: Record<string, string | string[] | Record<string, string>> =
    {};
  propertyInputKeys.forEach((key) => {
    const propertyName = key.replace("properties[", "").replace("]", "");
    properties[propertyName] = params[key];
  });
  return properties;
}

document.addEventListener("cart:updated", handlePfandCheck);

export async function handlePfandCheck() {
  const updatedCartData = await fetchCartData();

  const pfandCharge = updatedCartData.items.find(
    (item) => item.title === "Pfand",
  );
  const hasPfandPerItem = updatedCartData.items.some(
    (item) => item.properties && item.properties["Pfand"],
  );

  // Remove Pfand charge if it's the only item or no item has "Pfand"
  if (
    (updatedCartData.items.length === 1 && pfandCharge) ||
    (!hasPfandPerItem && pfandCharge)
  ) {
    await removePfandCharge(pfandCharge);
  }

  // Add Pfand charge if no "Pfand charge" but "Pfand" exists
  if (hasPfandPerItem && !pfandCharge) {
    await addPfandChargeWithExistingFunction();
  }
}

async function removePfandCharge(pfandCharge: CartItem) {
  if (!pfandCharge) {
    return;
  }

  const response = await fetch("/cart/change.js", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      id: pfandCharge.key,
      quantity: 0,
    }),
  });

  const updatedCartData = await response.json();

  document.dispatchEvent(
    new CustomEvent("cart:updated", { detail: updatedCartData }),
  );
}

async function addPfandChargeWithExistingFunction() {
  const pfandChargeVariantId = window.themeVars.config.pfandProduct;

  if (!pfandChargeVariantId) {
    return;
  }

  await sendAddToCart([
    {
      id: pfandChargeVariantId,
      quantity: 1,
      properties: {},
      selling_plan: "",
    },
  ]);
}
